import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import { Button } from './button';

/**
 * Paginación de una tabla.
 *
 * 🔴 Este bloque estaba escrito a mano **tres veces** —en el listado de
 * empresas, en el de usuarios y en la bandeja de bugs— con el mismo código y
 * diferencias chicas entre ellos. Además ninguno decía cuántos registros hay en
 * total: mostraban "Página 1 de 5", que no responde la pregunta que uno se hace
 * al abrir un listado.
 */

interface PaginationProps {
  /** Página actual, empezando en 1. */
  page: number;
  totalPages: number;
  /** Cuántos registros hay en total. Si no se sabe, se omite. */
  totalItems?: number;
  /** Cuántos entran por página, para calcular el rango que se está viendo. */
  pageSize?: number;
  onPageChange: (page: number) => void;
  /** Mientras carga, no se puede saltar de página. */
  disabled?: boolean;
  /** Cómo se llama lo que se está listando, en plural: "empresas", "usuarios". */
  itemName?: string;
}

export function Pagination({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  disabled = false,
  itemName = 'registros',
}: PaginationProps) {
  // Con una sola página no hace falta ocupar espacio con controles inertes.
  if (totalPages <= 1 && !totalItems) return null;

  const primero = pageSize ? (page - 1) * pageSize + 1 : null;
  const ultimo =
    pageSize && totalItems
      ? Math.min(page * pageSize, totalItems)
      : pageSize
        ? page * pageSize
        : null;

  return (
    <div className="flex flex-col items-center justify-between gap-3 border-t px-1 py-3 sm:flex-row">
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {totalItems !== undefined && primero !== null && ultimo !== null ? (
          <>
            Mostrando {formatoCantidad(primero)}–{formatoCantidad(ultimo)} de{' '}
            {formatoCantidad(totalItems)} {itemName}
          </>
        ) : totalItems !== undefined ? (
          <>
            {formatoCantidad(totalItems)} {itemName}
          </>
        ) : (
          <>
            Página {page} de {totalPages}
          </>
        )}
      </p>

      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={disabled || page <= 1}
            aria-label="Página anterior"
          >
            <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
            Anterior
          </Button>

          {/*
            El número de página va acá y no solo en el texto de la izquierda para
            que se lea junto a los controles que lo cambian.
          */}
          <span className="min-w-[5rem] text-center text-sm tabular-nums">
            {page} / {totalPages}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={disabled || page >= totalPages}
            aria-label="Página siguiente"
          >
            Siguiente
            <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default Pagination;
